import type { KnowledgeItem } from '@/types';

// 高炉炼铁知识库（15 条）
// 覆盖：工艺基础 / 关键参数 / 三大故障处置 / 设备维护 / 高级操作
export const blastfurnaceKnowledge: KnowledgeItem[] = [
  // ========== 工艺基础 ==========
  {
    id: 'BF-K001',
    tags: ['高炉', '工艺', '基础'],
    question: '高炉炼铁的基本原理是什么？',
    answer:
      '高炉是一座巨型密封反应塔：上部装入铁矿石+焦炭+熔剂，下部风口鼓入 1000°C 以上热风。' +
      '焦炭燃烧放热到 1500°C，同时生成还原气体 CO 把铁矿石里的铁还原成液态铁水，' +
      '铁水从底部出铁口放出装入鱼雷罐车送去炼钢，炉顶煤气（含 CO/CO₂）经净化后回用发电。' +
      '整个反应连续 5~10 年不停炉，一天出铁上万吨。',
  },
  {
    id: 'BF-K002',
    tags: ['焦比', 'COKE-RATIO', '关键参数'],
    question: '焦比是什么？为什么这么重要？',
    answer:
      '焦比 = 每吨铁水消耗的焦炭吨数，通常 0.35~0.55。焦炭是高炉唯一的固体燃料+还原剂，' +
      '同时它还要在炉内形成"焦柱"支撑上面几百吨料柱。焦比过低炉子会凉、料柱会塌；' +
      '焦比过高浪费成本+多排 CO₂。国内大高炉挑战目标是 0.30 以下，靠喷煤和富氧替代。',
  },
  {
    id: 'BF-K003',
    tags: ['送风', '热风炉', '关键参数'],
    question: '热风炉送风温度为什么要 1000°C 以上？',
    answer:
      '每提高 100°C 送风温度可节焦 15 kg/t 铁，还能提高冶炼强度。' +
      '热风炉用 3 座 1 组"两烧一送"轮换：两座在储热（烧煤气加热蓄热室格子砖），一座在送热风。' +
      '送风温度上限受热风管衬砖耐温限制，1250°C 是常规上限，1350°C 是极限。',
  },
  {
    id: 'BF-K004',
    tags: ['风量', 'MAF', '关键参数'],
    question: '风量是不是越大越好？',
    answer:
      '不是。风量正常 3000~4200 Nm³/min，代表冶炼强度。风量↑ 燃烧强 → 铁水多 → 效率高，' +
      '但风量太大会压料 → 料线不下降 → 悬料 → 甚至崩料炸炉。' +
      '风口理论燃烧温度也是控风的依据，通常保 2100~2300°C，太低炉凉、太高烧穿。',
  },
  {
    id: 'BF-K005',
    tags: ['铁水温度', 'T-tap', '关键参数'],
    question: '铁水温度控制在什么范围？',
    answer:
      '正常 1450~1510°C。低于 1450 铁水粘稠 —— 硫脱不掉、渣铁分不清、出铁口容易结瘤；' +
      '高于 1510 会加速出铁口衬砖烧损，也说明炉内热过剩，浪费焦炭。' +
      '现场判断：老师傅看铁水火焰颜色，橙红是低温，白亮才是好温度。',
  },

  // ========== 典型故障处置 ==========
  {
    id: 'BF-K101',
    tags: ['炉凉', '故障', '抢救'],
    question: '铁水温度掉到 1440°C 了怎么办？',
    answer:
      '这是"炉凉"预警。正确操作：①先观察铁水温度走势 25 秒（不要马上动）' +
      '②焦比从 0.42 提到 0.48（加焦增热）③等 60 秒让焦炭下到风口起效 ' +
      '④风量从 3200 加到 3600（加风助燃）⑤再等 90 秒观察铁水回升。' +
      '⚠️ 绝对不能：焦炭还没下就急着加风 —— 那是给冷炉子灌冷空气，越吹越凉。',
  },
  {
    id: 'BF-K102',
    tags: ['悬料', '故障', '高危'],
    question: '料线卡住不下降，炉腹温度反常上升，这是什么故障？怎么处理？',
    answer:
      '⚠️ 这是"悬料"—— 炉料在炉喉结拱、上部不下料、下部继续烧，是极危险的高危故障。' +
      '如不立即处置，压力憋高会发生"崩料"甚至炸炉。' +
      '正确操作：①立即把风量从 3800 降到 2800（降风减压）②等 45 秒让压力回落 ' +
      '③打开排烟囱泄压阀（辅助释压）④再等 60 秒观察料线是否恢复下降。' +
      '⚠️ 绝对不能：不降风继续硬吹 —— "你差点把老三高炉给炸了！"',
  },
  {
    id: 'BF-K103',
    tags: ['出铁口', '结瘤', '故障'],
    question: '出铁口结瘤怎么处理？',
    answer:
      '铁水流量掉到平时一半、装载速率降 50% —— 出铁口内壁积了冷铁瘤。' +
      '正确操作：①先把送风温度从 1050 提到 1150（让炉腹先热起来）②等 120 秒热量传到出铁口 ' +
      '③确认铁水温度回升到 1450 以上 ④用泥炮打开出铁口。' +
      '⚠️ 铁律：先热后敲。铁水温度不到 1450 就敲炮 —— "1380 度你敲什么！凉铁堵得更死"。',
  },
  {
    id: 'BF-K104',
    tags: ['崩料', '事故', '高危'],
    question: '什么是"崩料"？为什么这么可怕？',
    answer:
      '崩料 = 悬料的料拱突然崩塌，上部几百吨料柱瞬间垮到风口区。' +
      '后果：①炉内压力瞬间飙升 → 顶盖顶飞 ②大量未反应料入风口 → 冷炉、结瘤 ' +
      '③冲击炉衬 → 可能击穿炉壳 → 铁水漏出炸炉。' +
      '国内每次崩料事故都要写进行业教材。防崩料铁律：宁可"降风"错杀，不可"硬吹"漏放。',
  },
  {
    id: 'BF-K105',
    tags: ['探尺', '料线', '监测'],
    question: '探尺和料线在高炉里起什么作用？',
    answer:
      '探尺是从炉顶垂到料面的机械尺，每分钟测一次料面到炉顶的距离（就是"料线"），' +
      '正常 0.5~2.5m 之间稳步下降。料线在装料后被抬到 0.5，' +
      '料下降到 2.5 附近就要装下一批。如果料线卡住 30 秒不动 —— 悬料信号，立即降风检查。',
  },

  // ========== 设备维护 ==========
  {
    id: 'BF-K201',
    tags: ['出铁口', '泥炮', '维护'],
    question: '泥炮是什么？为什么高炉离不开它？',
    answer:
      '泥炮是液压驱动的大炮，出铁完后把炮泥（高铝耐火泥）射入出铁口封堵。' +
      '打泥量、打入深度、打入压力都要控制：打少了下次出铁时可能烧穿；' +
      '打多了泥不干透，下次开炮时不好敲开。老班组的绝活就是"看铁色调泥量"。',
  },
  {
    id: 'BF-K202',
    tags: ['炉衬', '寿命', '炉役'],
    question: '高炉炉衬能用多久？',
    answer:
      '现代大高炉一代炉役 12~20 年，一直不停炉。炉衬内层是碳素耐火砖+冷却壁，' +
      '外壁通冷却水（水温+压力都要严格监控）。炉役中要经历大修、中修、局部换砖。' +
      '判断炉衬寿命看：①冷却水温升 ②炉壳温度 ③耐火砖侵蚀曲线。',
  },
  {
    id: 'BF-K203',
    tags: ['鼓风机', '维护'],
    question: '高炉鼓风机为什么被称为"高炉的心脏"？',
    answer:
      '鼓风机一停高炉就凉，一凉损失几十万吨铁产量。' +
      '国内大高炉配 2 台 8 万 kW 轴流风机（一开一备），风量 3000~4200 Nm³/min。' +
      '轴瓦温度、振动、油压都要实时监控 —— 老师傅说：能听风机的声音就能知道毛病在哪。',
  },

  // ========== 高级操作 ==========
  {
    id: 'BF-K301',
    tags: ['富氧', '喷煤', '高级'],
    question: '富氧喷煤是什么操作？',
    answer:
      '富氧 = 送风里氧含量从 21%（普通空气）提高到 24~28%（掺纯氧）。' +
      '喷煤 = 从风口喷入煤粉部分替代焦炭。两者组合能：①提高燃烧温度 ②降低焦比到 0.30 以下 ' +
      '③增产 10~15%。但富氧不能突然拉大，容易导致管道燃烧、烧穿风口。',
  },
  {
    id: 'BF-K302',
    tags: ['顶压', '炉顶煤气', '高级'],
    question: '为什么现代高炉都用"高炉顶压"操作？',
    answer:
      '把炉顶压力从常压提高到 200~260 kPa（高压操作），有 4 大好处：' +
      '①煤气流速降 → 顺行性好 ②带走细粉少 → 除尘负荷降 ③单位料柱压强大 → 冶炼强度可提高 ' +
      '④炉顶煤气 TRT 透平回收能量。缺点：设备密封要求高，' +
      '悬料时顶压会瞬间飙升到 300 kPa 以上 —— 必须立刻打开泄压阀。',
  },
];
